import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Book, AudioLines, Eye, Check, Film, Plus, ChevronRight, Loader2 } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { API } from "../lib/constants";

export default function ProductionMode({ embedded = false, seriesOverride = null }) {
  const { isDark } = useTheme();
  const [seriesFilter, setSeriesFilter] = useState(seriesOverride || 'the-red-ink-chronicles');
  const [seasonProgress, setSeasonProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [seasonEpisodes, setSeasonEpisodes] = useState([]);
  const [loadingEps, setLoadingEps] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [batchUpdating, setBatchUpdating] = useState(false);
  const [projects, setProjects] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProgress();
    fetchProjects();
    const interval = setInterval(fetchProgress, 15000);
    return () => clearInterval(interval);
  }, [seriesFilter]);

  useEffect(() => {
    if (seriesOverride && seriesOverride !== seriesFilter) {
      setSeriesFilter(seriesOverride);
      setSelectedSeason(null);
      setSeasonEpisodes([]);
    }
  }, [seriesOverride]);

  const fetchProgress = async () => {
    try {
      const res = await axios.get(`${API}/episodes/season-progress?series=${seriesFilter}`);
      setSeasonProgress(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/production/projects`);
      setProjects(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error(e); }
  };

  const fetchSeasonEpisodes = async (season) => {
    setLoadingEps(true);
    try {
      const res = await axios.get(`${API}/episodes?series=${seriesFilter}&season=${season}&limit=150`);
      setSeasonEpisodes(res.data);
    } catch (e) { console.error(e); }
    finally { setLoadingEps(false); }
  };

  const updateEpisodeStatus = async (episodeId, status) => {
    setUpdatingStatus(episodeId);
    try {
      await axios.put(`${API}/episodes/${episodeId}/status`, { status });
      setSeasonEpisodes(prev => prev.map(ep => ep.id === episodeId ? { ...ep, production_status: status } : ep));
    } catch (e) { console.error(e); alert('Failed to update status'); }
    finally { setUpdatingStatus(null); }
  };

  const batchUpdateSeason = async (season, status) => {
    if (!window.confirm(`Set ALL episodes in Season ${season} to "${status}"?`)) return;
    setBatchUpdating(true);
    try {
      await axios.put(`${API}/episodes/batch-status`, { season, series: seriesFilter, status });
      fetchProgress();
      if (selectedSeason === season) fetchSeasonEpisodes(season);
    } catch (e) { console.error(e); }
    finally { setBatchUpdating(false); }
  };

  const totals = seasonProgress.reduce((acc, s) => ({
    total: acc.total + s.total,
    expanded: acc.expanded + s.expanded,
    thumbnails: acc.thumbnails + s.thumbnails,
    audio: acc.audio + s.audio,
    complete: acc.complete + s.complete
  }), { total: 0, expanded: 0, thumbnails: 0, audio: 0, complete: 0 });

  const seriesLabel = seriesFilter === 'signed-sealed-betrayed' ? 'SIGNED, SEALED, BETRAYED' : seriesFilter === 'the-notarys-shadow' ? "THE NOTARY'S SHADOW" : 'THE RED INK CHRONICLES';

  const STATUS_FLOW = ['script', 'assets', 'recording', 'mixing', 'complete'];
  const STATUS_COLORS = {
    script: { bg: 'bg-gray-600', text: 'text-gray-400', label: 'Script' },
    assets: { bg: 'bg-yellow-600', text: 'text-yellow-400', label: 'Assets' },
    recording: { bg: 'bg-blue-600', text: 'text-blue-400', label: 'Recording' },
    mixing: { bg: 'bg-purple-600', text: 'text-purple-400', label: 'Mixing' },
    complete: { bg: 'bg-green-600', text: 'text-green-400', label: 'Complete' },
  };

  if (loading) return (
    <div className={`${embedded ? 'py-12' : 'min-h-screen pt-20'} flex items-center justify-center`}>
      <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className={`${embedded ? 'pb-12' : 'min-h-screen pt-24 pb-20 px-6'} ${isDark ? '' : 'text-gray-900'}`} data-testid="production-mode">
      <div className={embedded ? '' : 'max-w-6xl mx-auto'}>
        {!embedded && (
          <header className="text-center mb-8">
            <h1 className="font-cinzel text-4xl md:text-5xl font-bold mb-3">PRODUCTION MODE</h1>
            <p className="text-gray-400 text-lg">From script to screen — manage your production pipeline</p>
          </header>
        )}

        {!embedded && (
          <div className="flex justify-center gap-3 mb-8" data-testid="prod-series-selector">
            {[
              { slug: 'the-red-ink-chronicles', label: 'The Red Ink Chronicles' },
              { slug: 'the-notarys-shadow', label: "The Notary's Shadow" },
              { slug: 'signed-sealed-betrayed', label: 'Signed, Sealed, Betrayed' }
            ].map(s => (
              <button
                key={s.slug}
                onClick={() => { setSeriesFilter(s.slug); setSelectedSeason(null); setSeasonEpisodes([]); }}
                className={`px-5 py-3 rounded-lg font-cinzel text-sm font-bold transition-all ${
                  seriesFilter === s.slug ? 'bg-red-600 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
                data-testid={`prod-series-${s.slug}-btn`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        <div className="mb-8 p-6 border border-white/10 rounded-lg bg-white/[0.02]" data-testid="overall-progress">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-anton text-lg tracking-wider">{seriesLabel}</h2>
            <span className="text-sm text-gray-400">{totals.total} episodes</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: 'Stories Expanded', val: totals.expanded, color: 'text-green-400', bar: 'bg-green-500' },
              { label: 'Thumbnails', val: totals.thumbnails, color: 'text-purple-400', bar: 'bg-purple-500' },
              { label: 'Audio Ready', val: totals.audio, color: 'text-blue-400', bar: 'bg-blue-500' },
              { label: 'Complete', val: totals.complete, color: 'text-red-400', bar: 'bg-red-500' },
            ].map(item => (
              <div key={item.label}>
                <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                <p className={`text-2xl font-bold ${item.color} font-mono`}>{item.val}<span className="text-sm text-gray-500">/{totals.total}</span></p>
                <div className="h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden">
                  <div className={`h-full ${item.bar} rounded-full transition-all`} style={{ width: `${totals.total > 0 ? (item.val/totals.total)*100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
          {[
            {
              title: "BOOK / eBOOK", color: "green", icon: <Book size={20} />,
              progress: totals.total > 0 ? Math.round((totals.expanded / totals.total) * 100) : 0,
              steps: [
                { label: "Scripts Written", done: true, detail: `${totals.total} episodes` },
                { label: "AI Story Expansion", done: totals.expanded === totals.total, detail: `${totals.expanded}/${totals.total}` },
                { label: "Production Script Export", done: false, detail: "T.I.N.G o LING format" },
                { label: "Export eBook", done: false, detail: "Per season or complete" },
              ],
              actions: [
                { label: "Export eBook", fn: () => window.open(`${API}/export/ebook?series=${seriesFilter}`, '_blank') },
                { label: "Export Production Script", fn: () => window.open(`${API}/export/production-script?series=${seriesFilter}`, '_blank') },
              ]
            },
            {
              title: "AUDIOBOOK", color: "purple", icon: <AudioLines size={20} />,
              progress: totals.total > 0 ? Math.round(((totals.expanded + totals.audio) / (totals.total * 2)) * 100) : 0,
              steps: [
                { label: "Stories Expanded", done: totals.expanded === totals.total, detail: `${totals.expanded}/${totals.total}` },
                { label: "Voice Profiles Created", done: false, detail: "Character voice bank" },
                { label: "TTS Audio Generation", done: totals.audio > 0, detail: `${totals.audio}/${totals.total}` },
                { label: "Multi-Track Mix", done: false, detail: "Narrator + Music + SFX" },
              ],
              actions: [
                { label: "Manage Voice Profiles", fn: () => navigate('/assets') },
                { label: "Character Voice Guide", fn: () => window.open(`${API}/export/character-voice-guide`, '_blank') },
              ]
            },
            {
              title: "VIDEO BOOK", color: "amber", icon: <Eye size={20} />,
              progress: totals.total > 0 ? Math.round(((totals.expanded + totals.thumbnails) / (totals.total * 2)) * 50) : 0,
              steps: [
                { label: "Stories Expanded", done: totals.expanded === totals.total, detail: `${totals.expanded}/${totals.total}` },
                { label: "Thumbnails/Scenes", done: totals.thumbnails === totals.total, detail: `${totals.thumbnails}/${totals.total}` },
                { label: "Audio Track Generation", done: false, detail: "TTS + character voices" },
                { label: "Video Assembly", done: false, detail: "Scenes + audio + music" },
              ],
              actions: [
                { label: "Go to Asset Manager", fn: () => navigate('/assets') },
                { label: "Export Production Script", fn: () => window.open(`${API}/export/production-script?series=${seriesFilter}`, '_blank') },
              ]
            }
          ].map((pipe, idx) => (
            <div key={idx} className={`border border-${pipe.color}-500/40 rounded-lg overflow-hidden`} data-testid={`pipeline-${pipe.title.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className={`bg-${pipe.color}-500/10 p-4 border-b border-${pipe.color}-500/40`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-${pipe.color}-400`}>{pipe.icon}</span>
                  <h3 className="font-anton text-sm tracking-wider">{pipe.title}</h3>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className={`h-full bg-${pipe.color}-500 rounded-full transition-all`} style={{ width: `${pipe.progress}%` }} />
                  </div>
                  <span className={`text-xs font-mono font-bold text-${pipe.color}-400`}>{pipe.progress}%</span>
                </div>
              </div>
              <div className="p-4">
                <div className="space-y-2 mb-4">
                  {pipe.steps.map((step, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${step.done ? 'bg-green-600' : 'bg-white/10'}`}>
                        {step.done ? <Check size={12} className="text-white" /> : <span className="text-[10px] text-gray-500">{i+1}</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-semibold ${step.done ? 'text-green-400' : ''}`}>{step.label}</p>
                        <p className="text-[10px] text-gray-600 truncate">{step.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="pt-3 border-t border-white/5 flex flex-col gap-1.5">
                  {pipe.actions.map((action, i) => (
                    <button key={i} onClick={action.fn}
                      className={`w-full px-3 py-1.5 text-xs font-semibold transition-colors rounded ${i === 0 ? `bg-${pipe.color}-500/10 text-${pipe.color}-400 hover:bg-${pipe.color}-500/20` : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                      data-testid={`action-${pipe.title.toLowerCase().replace(/\s+/g,'-')}-${i}`}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mb-8">
          <h2 className="font-anton text-lg tracking-wider mb-4">SEASON MANAGEMENT</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
            {seasonProgress.map(s => (
              <button
                key={s.season}
                onClick={() => { setSelectedSeason(s.season); fetchSeasonEpisodes(s.season); }}
                className={`border rounded-lg p-4 text-left transition-all ${
                  selectedSeason === s.season ? 'border-red-500/50 bg-red-950/20' : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.05]'
                }`}
                data-testid={`prod-season-${s.season}`}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="font-anton text-xs text-gray-400">S{s.season}</span>
                  <span className="text-[10px] text-gray-500">{s.total} eps</span>
                </div>
                <div className="space-y-1">
                  <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full" style={{ width: `${s.expand_pct}%` }} />
                  </div>
                  <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-500 rounded-full" style={{ width: `${s.thumb_pct}%` }} />
                  </div>
                </div>
                <div className="mt-2 text-center">
                  <span className={`text-xs font-mono font-bold ${s.complete_pct >= 80 ? 'text-green-400' : s.complete_pct >= 20 ? 'text-yellow-400' : 'text-gray-500'}`}>
                    {Math.round(s.complete_pct)}% complete
                  </span>
                </div>
              </button>
            ))}
          </div>

          {selectedSeason && (
            <div className="border border-white/10 rounded-lg p-4 bg-white/[0.02]" data-testid="season-episode-list">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-anton text-sm tracking-wider">SEASON {selectedSeason} EPISODES</h3>
                <div className="flex gap-2">
                  {STATUS_FLOW.map(status => (
                    <button
                      key={status}
                      onClick={() => batchUpdateSeason(selectedSeason, status)}
                      disabled={batchUpdating}
                      className={`px-2 py-1 text-[10px] font-bold rounded transition-all ${STATUS_COLORS[status].bg} text-white hover:opacity-80`}
                      data-testid={`batch-${status}-btn`}
                      title={`Set all to ${STATUS_COLORS[status].label}`}
                    >
                      All &rarr; {STATUS_COLORS[status].label}
                    </button>
                  ))}
                </div>
              </div>
              
              {loadingEps ? (
                <div className="text-center py-8"><div className="w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>
              ) : (
                <div className="space-y-1 max-h-[500px] overflow-y-auto">
                  {seasonEpisodes.map(ep => {
                    const currentStatus = ep.production_status || 'script';
                    const sc = STATUS_COLORS[currentStatus];
                    return (
                      <div key={ep.id} className="flex items-center gap-3 px-3 py-2 rounded hover:bg-white/[0.03] group" data-testid={`prod-ep-${ep.id}`}>
                        <span className="text-xs text-gray-600 font-mono w-12 flex-shrink-0">E{ep.episode_number}</span>
                        <p className="flex-1 text-sm truncate">{ep.title}</p>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          {ep.full_story && <span className="text-[9px] px-1 py-0.5 bg-green-600/20 text-green-500 rounded">Story</span>}
                          {ep.thumbnail_url && <span className="text-[9px] px-1 py-0.5 bg-purple-600/20 text-purple-500 rounded">Thumb</span>}
                          {ep.audio_url && <span className="text-[9px] px-1 py-0.5 bg-blue-600/20 text-blue-500 rounded">Audio</span>}
                        </div>
                        <select
                          value={currentStatus}
                          onChange={(e) => updateEpisodeStatus(ep.id, e.target.value)}
                          disabled={updatingStatus === ep.id}
                          className={`text-[11px] px-2 py-1 rounded border border-white/15 font-bold cursor-pointer bg-[#1a1a1a] text-white`}
                          data-testid={`prod-status-${ep.id}`}
                        >
                          {STATUS_FLOW.map(st => (
                            <option key={st} value={st}>{STATUS_COLORS[st].label}</option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Production Projects */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-anton text-lg tracking-wider">PRODUCTION PROJECTS</h2>
            <span className="text-xs text-gray-500">{projects.length} project{projects.length !== 1 ? 's' : ''}</span>
          </div>
          {projects.length === 0 ? (
            <div className="border border-white/10 border-dashed rounded-lg p-8 text-center">
              <Film size={24} className="mx-auto text-gray-600 mb-2" />
              <p className="text-gray-500 text-sm">No production projects yet</p>
              <p className="text-gray-600 text-xs mt-1">Create projects from the Cast Roster tab</p>
            </div>
          ) : (
            <div className="space-y-2">
              {projects.slice(0, 5).map(p => (
                <div key={p.id} className="flex items-center gap-4 px-4 py-3 border border-white/10 rounded-lg bg-white/[0.02] hover:bg-white/[0.04] transition-all" data-testid={`project-${p.id}`}>
                  <Film size={16} className="text-red-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate">{p.title}</p>
                    <p className="text-[10px] text-gray-500">{p.project_type} · {p.cast?.length || 0} characters · {p.status || 'draft'}</p>
                  </div>
                  <ChevronRight size={14} className="text-gray-600" />
                </div>
              ))}
              {projects.length > 5 && <p className="text-xs text-gray-500 text-center">+{projects.length - 5} more</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
